import type React from 'react'
import { GripVertical, Radio } from 'lucide-react'
import { INFRA_DEVICE_TYPES, OBSTACLE_TYPES } from '../../lib/floorPlan/materials'
import type { FloorDeviceType } from '../../lib/floorPlan/types'

export const FLOOR_PALETTE_MIME = 'application/x-mgopen-floor-item'

export type PalettePayload =
  | { kind: 'obstacle'; type: string }
  | { kind: 'device'; type: FloorDeviceType }
  | { kind: 'ap'; apName: string; deviceId?: string }

export function paletteDragStart(e: React.DragEvent, payload: PalettePayload) {
  e.dataTransfer.setData(FLOOR_PALETTE_MIME, JSON.stringify(payload))
  e.dataTransfer.effectAllowed = 'copy'
}

export function parsePaletteDrop(raw: string): PalettePayload | null {
  if (!raw) return null
  try {
    const p = JSON.parse(raw) as PalettePayload
    if (p.kind === 'obstacle' && typeof p.type === 'string') return p
    if (p.kind === 'device' && typeof p.type === 'string') return p
    if (p.kind === 'ap' && typeof p.apName === 'string') return p
    return null
  } catch {
    return null
  }
}

type Props = {
  inventoryAps: { id: string; name: string; model?: string }[]
  compact?: boolean
}

export default function FloorPlanPalette({ inventoryAps, compact = false }: Props) {
  return (
    <div className={`floor-palette ${compact ? 'floor-palette--compact' : ''}`}>
      <p className="floor-palette-title">Drag onto the floor map</p>

      <div className="floor-palette-section">
        <p className="floor-palette-label">Obstacles</p>
        <div className="floor-palette-grid">
          {OBSTACLE_TYPES.map(o => (
            <div
              key={o.id}
              draggable
              title={`${o.label} · ${o.attenuationDb} dB`}
              className="floor-palette-item"
              onDragStart={e => paletteDragStart(e, { kind: 'obstacle', type: o.id })}
            >
              <GripVertical size={12} className="floor-palette-grip" />
              <span>{o.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="floor-palette-section">
        <p className="floor-palette-label">Infrastructure</p>
        <div className="floor-palette-grid">
          {INFRA_DEVICE_TYPES.slice(0, 6).map(d => (
            <div
              key={d.id}
              draggable
              title={d.label}
              className="floor-palette-item"
              onDragStart={e => paletteDragStart(e, { kind: 'device', type: d.id })}
            >
              <GripVertical size={12} className="floor-palette-grip" />
              <span>{d.label}</span>
            </div>
          ))}
        </div>
      </div>

      {inventoryAps.length > 0 && (
        <div className="floor-palette-section">
          <p className="floor-palette-label">Access points ({inventoryAps.length})</p>
          <div className="floor-palette-grid max-h-48 overflow-y-auto [scrollbar-width:thin]">
            {inventoryAps.map(ap => (
              <div
                key={ap.id}
                draggable
                title={ap.name}
                className="floor-palette-item floor-palette-item--ap"
                onDragStart={e => paletteDragStart(e, { kind: 'ap', apName: ap.name, deviceId: ap.id })}
              >
                <Radio size={12} />
                <span>{ap.name.length > 18 ? `${ap.name.slice(0, 16)}…` : ap.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="floor-palette-hint">Use Draw tools for walls and rooms · Select to edit or delete</p>
    </div>
  )
}
